import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';

import { useDisclosure } from '@chakra-ui/hooks';

import { washHub } from 'store/slices';

import { updateContact } from 'api/contacts';
import { useMutation, useQueryClient } from 'react-query';

import { successToast } from 'utils/successToast';

import {
  RefType,
  EditCustomerModalProps,
} from './EditCustomerContactModal.props';
import { Contact, ContactStatusEnum } from 'types/user';

import EditCustomerContactModalView from './EditCustomerContactModal.view';

const Container = forwardRef<RefType, EditCustomerModalProps>((_, ref) => {
  const queryClient = useQueryClient();
  const washHubs = washHub.selectors.useAllWashHubs();
  const washHubId = washHub.selectors.useSelectedWashHubId();

  const [contact, setContact] = useState<Contact | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();

  useImperativeHandle(
    ref,
    () => ({
      onOpen: (_contact) => {
        setContact(_contact);

        onOpen();
      },
    }),
    [onOpen],
  );

  const { mutate, isLoading: isCreating } = useMutation(updateContact, {
    onSuccess: () => {
      successToast('Customer Contact Updated Successfully');

      queryClient.invalidateQueries(`fetch-contacts-${washHubId}`);
      queryClient.invalidateQueries(`fetch-contacts-archived-${washHubId}`);

      queryClient.invalidateQueries(`fetch-contacts-${washHubId}-select`, {
        exact: true,
      });

      onClose();
    },
  });

  const _props = {
    contact,
    isOpen,
    onClose,
    washHubs,
    isCreating,
    onUpdate: async (payload: any) => {
      const { isArchived, ...restPayload } = payload;

      const updatePayload: any = {
        ...restPayload,
        _id: contact?._id,
      };

      // Set status based on the toggle
      if (isArchived) {
        updatePayload.status = ContactStatusEnum.ARCHIVED;
      } else {
        updatePayload.status = ContactStatusEnum.ACTIVE;
      }

      mutate(updatePayload);
    },
  };

  return <EditCustomerContactModalView {..._props} />;
});

const EditCustomerContactModal = memo(Container);

export default EditCustomerContactModal;
